import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Phone, Loader2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { trackContact } from "../lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RIDE_TYPES = ["Standard", "Executive", "Airport", "Maxi Van"];
const CONTACT_PHONE = "1300 582 485";
const CONTACT_TEL = "1300582485";
const CONTACT_EMAIL = "sydneyasaptaxis@gmail.com";

export const useBooking = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/bookings`, payload);
      setResult(data);
      trackContact();
      return data;
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        `Booking failed — please call ${CONTACT_PHONE} or email ${CONTACT_EMAIL}.`;
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { loading, result, error, submit, close: () => setResult(null) };
};

export const BookingModal = ({ result, onClose }) => (
  <AnimatePresence>
    {result && (
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-5"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          data-testid="booking-success-modal"
          className="relative bg-ink-800 border border-gold-400/30 rounded-2xl p-8 max-w-md w-full text-center gold-glow"
          initial={{ scale: 0.92, y: 24 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 12, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
        >
          <CheckCircle2 className="mx-auto text-gold-400 mb-4" size={48} />
          <h3 className="font-display text-2xl font-extrabold text-white mb-2">Taxi booked</h3>
          <p className="text-sm text-[#99A1B3] mb-5">
            Your booking is with our dispatcher now — a driver is being assigned. Keep your reference handy.
          </p>
          <p className="font-mono text-2xl text-gold-300 tracking-widest mb-6" data-testid="booking-reference">
            {result.reference}
          </p>
          <button
            data-testid="booking-modal-close-button"
            onClick={onClose}
            className="w-full rounded-full bg-gold-400 text-ink font-bold py-3 hover:bg-gold-300 transition-colors duration-200"
          >
            Done
          </button>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const Err = ({ error }) =>
  error ? (
    <p data-testid="booking-form-error" className="flex items-center gap-2 text-sm text-red-400 mt-3">
      <AlertTriangle size={14} /> {error}
    </p>
  ) : null;

export const BookingForm = ({ variant = "compact" }) => {
  const full = variant === "full";
  const tid = full ? "booking-full" : "booking";
  const { loading, result, error, submit, close } = useBooking();

  const defaultTime = () => {
    const d = new Date(Date.now() + 45 * 60000 - new Date().getTimezoneOffset() * 60000);
    return d.toISOString().slice(0, 16);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    if (full) {
      await submit({
        name: fd.get("name"),
        phone: fd.get("phone"),
        email: fd.get("email") || undefined,
        pickup: fd.get("pickup"),
        destination: fd.get("destination"),
        pickup_time: fd.get("pickup_time") || "ASAP",
        passengers: Number(fd.get("passengers") || 1),
        ride_type: fd.get("ride_type") || "Standard",
        notes: fd.get("notes") || undefined,
      });
    } else {
      await submit({
        name: fd.get("name"),
        phone: fd.get("phone"),
        pickup: fd.get("pickup"),
        destination: fd.get("destination"),
        pickup_time: "ASAP",
        passengers: 1,
        ride_type: "Standard",
      });
    }
    e.target.reset();
  };

  return (
    <>
      <form
        onSubmit={onSubmit}
        data-testid={full ? "booking-full-form" : "hero-booking-form"}
        className="space-y-4"
      >
        <div className={full ? "grid sm:grid-cols-2 gap-4" : "space-y-4"}>
          <div>
            <label className="field-label" htmlFor={`${tid}-name`}>Full name</label>
            <input
              id={`${tid}-name`}
              data-testid="booking-input-name"
              name="name"
              className="field"
              placeholder="Alex Morgan"
              required
              minLength={2}
              maxLength={80}
            />
          </div>
          <div>
            <label className="field-label" htmlFor={`${tid}-phone`}>Phone</label>
            <input
              id={`${tid}-phone`}
              data-testid="booking-input-phone"
              name="phone"
              type="tel"
              className="field"
              placeholder="0412 345 678"
              required
            />
          </div>
        </div>
        <div>
          <label className="field-label" htmlFor={`${tid}-pickup`}>Pickup address</label>
          <input
            id={`${tid}-pickup`}
            data-testid="booking-input-pickup"
            name="pickup"
            className="field"
            placeholder="200 George St, Sydney CBD"
            required
          />
        </div>
        <div>
          <label className="field-label" htmlFor={`${tid}-destination`}>Destination</label>
          <input
            id={`${tid}-destination`}
            data-testid="booking-input-destination"
            name="destination"
            className="field"
            placeholder="Sydney Airport T1, Mascot"
            required
          />
        </div>

        {full && (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="field-label" htmlFor={`${tid}-datetime`}>Pickup date &amp; time</label>
                <input
                  id={`${tid}-datetime`}
                  data-testid="booking-input-date-time"
                  name="pickup_time"
                  type="datetime-local"
                  className="field"
                  defaultValue={defaultTime()}
                  required
                />
              </div>
              <div>
                <label className="field-label" htmlFor={`${tid}-passengers`}>Passengers</label>
                <select
                  id={`${tid}-passengers`}
                  data-testid="booking-input-passengers"
                  name="passengers"
                  className="field"
                  defaultValue="1"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="field-label" htmlFor={`${tid}-ridetype`}>Ride type</label>
                <select
                  id={`${tid}-ridetype`}
                  data-testid="booking-input-ride-type"
                  name="ride_type"
                  className="field"
                  defaultValue="Standard"
                >
                  {RIDE_TYPES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor={`${tid}-email`}>Email (for receipt — optional)</label>
                <input
                  id={`${tid}-email`}
                  data-testid="booking-input-email"
                  name="email"
                  type="email"
                  className="field"
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <div>
              <label className="field-label" htmlFor={`${tid}-notes`}>Notes for the driver (optional)</label>
              <input
                id={`${tid}-notes`}
                data-testid="booking-input-notes"
                name="notes"
                className="field"
                placeholder="Gate code, child seat, luggage…"
                maxLength={500}
              />
            </div>
          </>
        )}

        <button
          type="submit"
          data-testid={full ? "booking-full-submit-button" : "booking-submit-button"}
          disabled={loading}
          className="w-full rounded-full bg-gold-400 text-ink font-bold py-3.5 flex items-center justify-center gap-2 hover:bg-gold-300 transition-colors duration-200 disabled:opacity-60"
        >
          {loading ? (
            <><Loader2 size={18} className="animate-spin" /> Booking your taxi…</>
          ) : full ? (
            "Book a Taxi Now"
          ) : (
            "Book a Taxi Now — ASAP"
          )}
        </button>
        <Err error={error} />
        {!full && (
          <p className="text-center text-xs text-[#626B7F]">
            Prefer to talk?{" "}
            <a href={`tel:${CONTACT_TEL}`} onClick={trackContact} className="text-gold-300 underline underline-offset-2">
              Call {CONTACT_PHONE}
            </a>
          </p>
        )}
      </form>
      <BookingModal result={result} onClose={close} />
    </>
  );
};

export const CallPanel = ({ testId = "call-command-center-button" }) => (
  <a
    href={`tel:${CONTACT_TEL}`}
    onClick={trackContact}
    data-testid={testId}
    className="group relative flex flex-col items-center justify-center gap-3 rounded-full bg-gold-400 text-ink font-display font-extrabold text-xl sm:text-2xl py-6 px-8 hover:bg-gold-300 transition-colors duration-200 overflow-hidden"
  >
    <span className="absolute inset-0 rounded-full border-2 border-gold-400 animate-pulse-ring" aria-hidden="true" />
    <span className="flex items-center gap-3">
      <Phone size={26} /> CALL NOW — {CONTACT_PHONE}
    </span>
    <span className="font-body font-semibold text-sm tracking-wide opacity-80">
      One tap. A dispatcher answers in seconds.
    </span>
  </a>
);
